import { useEffect, useRef } from 'react';

// Fires `fetchFn` immediately on mount/enable, then again every `intervalMs`.
// Skips ticks while document.hidden so we don't burn cycles on backgrounded
// tabs; a tick missed while hidden fires as a catch-up on the next
// visibilitychange back to visible, so refocusing never shows data staler
// than one interval. `cancelled` stops new ticks from being scheduled after
// teardown, but it does NOT abort an already-started fetch — consumers must
// guard their own setState against a stale in-flight response.

export default function usePolling(
  fetchFn,
  { intervalMs = 30000, enabled = true, skipFirstTick = false } = {},
) {
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let missedWhileHidden = false;

    const tick = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) {
        missedWhileHidden = true;
        return;
      }
      missedWhileHidden = false;
      Promise.resolve()
        .then(() => fetchRef.current())
        .catch(() => {
          // Polling failures shouldn't bubble — callers surface their own
          // error UI from the fetchFn if needed.
        });
    };

    const onVisibilityChange = () => {
      if (document.hidden || !missedWhileHidden) return;
      tick();
    };

    // Default: fire immediately on enable so callers waiting on a fresh
    // state transition (e.g. the confirm poller after a just-submitted
    // reroute) see it on the first tick rather than after a full interval.
    // Pure freshness heartbeats opt out via `skipFirstTick` so they don't
    // double-fetch right after the initial mount-time fetch.
    if (!skipFirstTick) tick();
    const handle = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, intervalMs, skipFirstTick]);
}
